import React, { useEffect, useState } from 'react'
import { Argument as ClassName } from 'classnames'

import { isUserHasAccessToConnector } from '../is-user-has-access-to-connector'
import { OcppAuraConnectorControl } from '../ocpp-aura-connector-control'
import { RemoteControlWidget } from '../remote-control-widget'
import { RemoteOcppDimmerControl } from '../remote-ocpp-dimmer-control'
import { SelectRfidTagDialog } from '../../common/dialogs/select-rfid-tag-dialog'
import {
    ChargePoint,
    Connector,
    useChargePointOverrideScheduleForOngoingChargingSessionMutation,
    useChargePointRemoteStartMutation,
    useChargePointRemoteStopMutation,
    useGreenChargingChargePointStatusQuery,
    useChargePointUpdateMutation,
} from '../../common/redux/api/web-api'
import { remoteStopIsAvailable } from '../get-connector-state/get-connector-state'
import { isAdminDomain } from '../currentDomain'
import { showErrorMessagesForIgnoreSchedule } from '../../common/utils/chargePointUtils'
import useText from '../../common/hooks/useText'
import { useToast } from '../../common/uikit/ToastProvider'

export type ChargePointOcppAuraControlWidgetProps = {
    isReadOnly?: boolean
    userId?: string
    chargePoint: ChargePoint
    className?: ClassName
    style?: React.CSSProperties
}

export function ChargePointOcppAuraControlWidget({
    userId,
    isReadOnly = false,
    chargePoint,
}: ChargePointOcppAuraControlWidgetProps): React.ReactElement {
    const isOnline = chargePoint.chargePointStatus !== 'Offline'
    const [remoteStart] = useChargePointRemoteStartMutation()
    const [remoteStop] = useChargePointRemoteStopMutation()
    const { data: chargePointStatus } = useGreenChargingChargePointStatusQuery({ chargePointId: chargePoint.id })
    const [selectRfidDialogIsOpen, setSelectRfidDialogIsOpen] = useState(false)
    const [currentConnector, setCurrentConnector] = useState(1)
    const [overrideSchedule] = useChargePointOverrideScheduleForOngoingChargingSessionMutation()
    const [updateChargePoint] = useChargePointUpdateMutation()
    const toast = useToast()

    const [connectors, setConnectors] = useState<Connector[]>(chargePoint.connectors ?? [])

    const text = useText()

    useEffect(() => {
        setConnectors(chargePoint.connectors ?? [])
    }, [chargePoint.connectors])

    const handleRemoteStart = async (connectorId: number, rfidTag?: string) => {
        await remoteStart({
            chargePointId: chargePoint.id,
            connectorId,
            rfidTag,
        })
            .unwrap()
            .then(() => toast.notifySuccess(text('msgRemoteStartSuccess')))
            .catch(() => toast.notifyError(text(isAdminDomain ? 'msgRemoteStartAdminFailed' : 'msgRemoteStartFailed')))
    }

    const handleRemoteControl = async (connectorId: number, isNfcLockOn: boolean) => {
        setCurrentConnector(connectorId)
        const connector = chargePoint?.connectors?.find((connector) => connector.connectorId === connectorId)
        if (remoteStopIsAvailable(connector ?? null)) {
            await remoteStop({
                chargePointId: chargePoint.id,
                connectorId,
            })
                .unwrap()
                .then(() => toast.notifySuccess(text('msgRemoteStopSuccess')))
                .catch(() =>
                    toast.notifyError(text(isAdminDomain ? 'msgRemoteStopAdminFailed' : 'msgRemoteStopFailed'))
                )
        } else {
            if (isNfcLockOn) {
                setSelectRfidDialogIsOpen(true)
            } else if (connector?.defaultNfcTagId) {
                handleRemoteStart(connectorId, connector?.defaultNfcTagId)
            } else {
                toast.notifyError(text('msgRemoteStartFailed'))
            }
        }
    }

    const handleOverrideSchedule = async (connectorId: number) => {
        setCurrentConnector(connectorId)

        await overrideSchedule({
            chargePointId: chargePoint.id,
            connectorId: connectorId,
        })
            .unwrap()
            .then(() => {
                toast.notifySuccess(text('msgIgnoreScheduleSuccess'))
            })
            .catch((error) => {
                toast.notifyError(showErrorMessagesForIgnoreSchedule(error.data.error))
            })
    }

    const handleConnectorUpdate = (connectorId: number, updatedConnector: Connector) => {
        setConnectors((prev) => prev.map((c) => (c.connectorId === connectorId ? updatedConnector : c)))

        const updatedConnectors = connectors.map((c) => (c.connectorId === connectorId ? updatedConnector : c))

        updateChargePoint({
            chargePoint: {
                ...chargePoint,
                connectors: updatedConnectors,
            },
        })
            .unwrap()
            .catch(() => {
                // Revert the optimistic update on error
                setConnectors(chargePoint.connectors ?? [])
                toast.notifyError(text('msgFailedToUpdateAvailability'))
            })
    }

    return (
        <RemoteControlWidget
            greenChargingStatus={chargePointStatus?.greenChargingStatus}
            isReadOnly={isReadOnly}
            chargePoint={chargePoint}
            title={'lblAuraControl'}
        >
            {connectors.map((connector) => {
                if (!isUserHasAccessToConnector(connector, chargePoint?.ownerId, userId)) return null
                return (
                    <OcppAuraConnectorControl
                        connector={connector}
                        isReadOnly={isReadOnly}
                        isOnline={isOnline}
                        chargePoint={chargePoint}
                        key={connector.connectorId}
                        onRemoteControl={({ connectorId, isNfcLockOn }) =>
                            handleRemoteControl(connectorId, isNfcLockOn)
                        }
                        handleOverrideSchedule={handleOverrideSchedule}
                        onConnectorUpdate={handleConnectorUpdate}
                    />
                )
            })}
            {isUserHasAccessToConnector(connectors[0], chargePoint?.ownerId, userId) &&
                isUserHasAccessToConnector(connectors[1], chargePoint.ownerId, userId) && (
                    <RemoteOcppDimmerControl chargePoint={chargePoint} isReadOnly={isReadOnly} />
                )}
            <SelectRfidTagDialog
                visible={selectRfidDialogIsOpen}
                chargePointId={chargePoint.id}
                connectorId={currentConnector}
                onStartCharging={(rfidTag) => {
                    setSelectRfidDialogIsOpen(false)
                    handleRemoteStart(currentConnector, rfidTag)
                }}
                onHide={() => setSelectRfidDialogIsOpen(false)}
            />
        </RemoteControlWidget>
    )
}
